import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import Accordion from 'react-bootstrap/Accordion';
import s from './UnitCompanionServiceLogDetail.module.scss';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import GoBack from '../WSAControls/GoBack/GoBack';
import MaintenanceContentTemplate from '../Maintenance/MaintenanceContentTemplate';
import { getUnitCompanionLogServicing, getCurrentTimezone } from '../../selectors/index';
import { initialUnitCompanionLogServicingState } from '../../reducers/initialState';
import { getDateInDDMMYYYHHMMSSFormat } from '../../utils';
import {
  describeUnitCompanionLogServicingThunk,
  getUnitCompanionLogServicingProcedureThunk,
  cleanUpThunk
} from '../../actions/unitCompanionLogServicing';

const UnitCompanionServiceLogDetail = ({ intl }) => {
  const dispatch = useDispatch();
  const { unitGuidedServiceSessionId } = useParams();

  const unitCompanionLogServicing = useSelector(state => getUnitCompanionLogServicing(state));
  const currentTimezone = useSelector(state => getCurrentTimezone(state));

  useEffect(() => {
    dispatch(describeUnitCompanionLogServicingThunk(unitGuidedServiceSessionId));
  }, [describeUnitCompanionLogServicingThunk, dispatch, unitGuidedServiceSessionId]);

  useEffect(() => {
    return () => {
      dispatch(cleanUpThunk({ ...initialUnitCompanionLogServicingState }));
    };
  }, []);

  const session = unitCompanionLogServicing.selectedSession || {};
  const procedures = unitCompanionLogServicing.procedures || [];
  const procedureDetails = unitCompanionLogServicing.procedureDetails || {};
  const loadingProcedureId = unitCompanionLogServicing.loadingProcedureId;

  let messageId = unitCompanionLogServicing.displayMessageCode || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const backToText = intl.formatMessage({ id: 'unitServiceDetail.back', defaultMessage: 'BACK' });
  const heading = intl.formatMessage({
    id: 'maintenance.companionServiceLog.viewSession',
    defaultMessage: 'Companion Service Log Session'
  });

  // alwaysOpen lets every procedure be expanded independently rather than
  // opening one collapsing the rest - with it, onSelect's eventKey is the
  // array of all currently-open keys, so fetch for any of them not already
  // loaded (covers opening a second/third one without re-fetching the first).
  const onAccordionSelect = eventKeys => {
    (eventKeys || []).forEach(eventKey => {
      if (!procedureDetails[eventKey]) {
        dispatch(getUnitCompanionLogServicingProcedureThunk(eventKey));
      }
    });
  };

  return (
    <div className={s.unitCompanionServiceLogDetail}>
      <MaintenanceContentTemplate selectedPage="companionServiceLog">
        <div className={s.contentWrapper}>
          {unitCompanionLogServicing.isLoading && <LoadingSpinner />}
          <Banner
            failureText={messageText}
            showBanner={unitCompanionLogServicing.showBanner}
            status={unitCompanionLogServicing.isOpSuccessful}
            successText={messageText}
          />
          <div className={s.detailHeader}>
            <GoBack defaultLink={'/maintenance/companionservicelog'}>
              &lt; &nbsp;
              {backToText}
            </GoBack>
            <h3>{heading}</h3>
          </div>

          <div className={s.detailContent}>
            <h4 className={s.subHeading}>
              <FormattedMessage id="maintenance.companionServiceLog.sessionDetails" defaultMessage="Session Details" />
            </h4>
            <Form className={s.viewRow}>
              <Row>
                <Col xs={12} lg={3}>
                  <Form.Group>
                    <Form.Label>
                      <FormattedMessage id="maintenance.companionServiceLog.unit" defaultMessage="Unit" />
                    </Form.Label>
                  </Form.Group>
                </Col>
                <Col>{session.UnitName || '-'}</Col>
              </Row>
              <Row>
                <Col xs={12} lg={3}>
                  <Form.Group>
                    <Form.Label>
                      <FormattedMessage id="maintenance.companionServiceLog.unitSerialNumber" defaultMessage="Unit Serial Number" />
                    </Form.Label>
                  </Form.Group>
                </Col>
                <Col>{session.UnitSerialNumber || '-'}</Col>
              </Row>
              <Row>
                <Col xs={12} lg={3}>
                  <Form.Group>
                    <Form.Label>
                      <FormattedMessage id="maintenance.companionServiceLog.started" defaultMessage="Started" />
                    </Form.Label>
                  </Form.Group>
                </Col>
                <Col>
                  {getDateInDDMMYYYHHMMSSFormat(session.StartedAt, currentTimezone)} ({session.StartedBy || '-'})
                </Col>
              </Row>
              <Row>
                <Col xs={12} lg={3}>
                  <Form.Group>
                    <Form.Label>
                      <FormattedMessage id="maintenance.companionServiceLog.ended" defaultMessage="Ended" />
                    </Form.Label>
                  </Form.Group>
                </Col>
                <Col>
                  {getDateInDDMMYYYHHMMSSFormat(session.EndedAt, currentTimezone)} ({session.EndedBy || '-'})
                </Col>
              </Row>
            </Form>
          </div>

          <div className={s.detailContent}>
            <h4 className={s.subHeading}>
              <FormattedMessage id="maintenance.companionServiceLog.procedures" defaultMessage="Procedures" />
            </h4>
            <Accordion alwaysOpen onSelect={onAccordionSelect}>
              {procedures.map(procedure => (
                <Accordion.Item eventKey={String(procedure.GuidedServiceProcedureId)} key={procedure.GuidedServiceProcedureId}>
                  <Accordion.Header>
                    {procedure.ProcedureName || procedure.ProcedureId}
                    {' - '}
                    {getDateInDDMMYYYHHMMSSFormat(procedure.EndedAt, currentTimezone)}
                  </Accordion.Header>
                  <Accordion.Body>
                    <ProcedureDetail
                      procedure={procedure}
                      detail={procedureDetails[procedure.GuidedServiceProcedureId]}
                      // loadingProcedureId comes from the Accordion's eventKey (always a
                      // string); GuidedServiceProcedureId is a number from the SQL result -
                      // compare as strings so this actually matches.
                      isLoading={String(loadingProcedureId) === String(procedure.GuidedServiceProcedureId)}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </div>
        </div>
      </MaintenanceContentTemplate>
    </div>
  );
};

const ProcedureDetail = ({ procedure, detail, isLoading }) => {
  return (
    <div>
      {procedure.Comments && (
        <Row className={s.procedureComments}>
          <Col xs={12} lg={2}>
            <Form.Label>
              <FormattedMessage id="maintenance.companionServiceLog.comments" defaultMessage="Comments" />
            </Form.Label>
          </Col>
          <Col>{procedure.Comments}</Col>
        </Row>
      )}

      {isLoading && (
        <div className={s.procedureLoading}>
          <LoadingSpinner centeredLoading={false} />
        </div>
      )}

      {!isLoading && detail && (
        <>
          {detail.actions && detail.actions.length > 0 && (
            <Table size="sm">
              <thead>
                <tr>
                  <th>
                    <FormattedMessage id="maintenance.companionServiceLog.action" defaultMessage="Action" />
                  </th>
                  <th>
                    <FormattedMessage id="maintenance.companionServiceLog.value" defaultMessage="Value" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.actions.map((action, index) => (
                  <tr key={index}>
                    <td>{action.Label}</td>
                    <td>
                      {action.Value}
                      {action.Unit ? ` ${action.Unit}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {detail.photos && detail.photos.length > 0 && (
            <div className={s.photoGrid}>
              {detail.photos.map(photo => (
                <a href={photo.Url} target="_blank" rel="noreferrer" key={photo.GuidedServicePhotoId} className={s.photoTile}>
                  <img src={photo.Url} alt={photo.StepId} />
                  <div className={s.stepId}>{photo.StepId}</div>
                </a>
              ))}
            </div>
          )}

          {(!detail.actions || detail.actions.length === 0) && (!detail.photos || detail.photos.length === 0) && (
            <FormattedMessage id="maintenance.companionServiceLog.noProcedureData" defaultMessage="No actions or photos recorded." />
          )}
        </>
      )}
    </div>
  );
};

export default injectIntl(UnitCompanionServiceLogDetail);
